// displaying actions and handling input

import React, { Component } from "react";
import Button from "../../../components/Button";
import WorkflowActionContainer from "../WorkflowActionContainer/WorkflowActionContainer";
import stepShouldSwitchAutomatically from "../../../../redux/modules/workflow/stepShouldSwitchAutomatically";
import { style } from "typestyle";

import { workflowStepActionsInputsCN } from "./styles";

import type {
  WorkflowStep,
  WorkflowAction,
} from "../../../../redux/modules/workflow/reducer";

interface Props {
  step: WorkflowStep;
  disabled?: boolean;
  collapsed?: boolean;
  onSubmit: (actions: WorkflowAction[]) => void;
}

interface State {
  actionValues: { [key: string]: { value: any } };
}

export default class WorkflowActions extends Component<Props, State> {
  UNSAFE_componentWillMount() {
    const { actions } = this.props.step;

    const noConfirmActions = actions.filter(
      ({ type }) => !isConfirmAction(type),
    );
    const actionValues = noConfirmActions.reduce((acc, curr) => {
      return {
        ...acc,
        [curr.id]: {
          value: curr.value || curr.defaultValue || null,
        },
      };
    }, {});

    this.setState({
      actionValues,
    });
  }

  handleActionInput = (actionId: string, actionType: string, value: string) => {
    if (actionType === "CONFIRM") {
      return;
    }

    this.setState(
      (prevState) => ({
        actionValues: {
          ...prevState.actionValues,
          [actionId]: { value },
        },
      }),
      () => {
        if (
          stepShouldSwitchAutomatically(this.props.step) &&
          actionIsTrigger(actionType) &&
          this.actionsAreValid()
        ) {
          this.handleStepSubmit();
        }
      },
    );
  };

  handleStepSubmit = (confirmActionId: string = "next") => {
    const { actions } = this.props.step;

    const confirmAction = {
      id: confirmActionId,
      value: "ok",
    };

    const submitActions = Object.keys(this.state.actionValues).map(
      (actionId) => {
        const value = this.state.actionValues[actionId].value;
        const action = actions.find(({ id }) => id === actionId);
        return {
          ...action,
          value,
        };
      },
    );

    // @ts-ignore action types seem to be quite messy
    this.props.onSubmit([...submitActions, confirmAction]);
  };

  actionsAreValid() {
    const { actionValues } = this.state;
    const keys = Object.keys(actionValues);
    for (let i = 0; i < keys.length; i++) {
      const actionId = keys[i];
      if (
        actionValues[actionId].value === null &&
        !this.actionIsOptional(actionId)
      ) {
        return false;
      }
    }
    return true;
  }

  actionIsOptional(actionId: string) {
    const { actions } = this.props.step;
    const action = actions.find(({ id }) => actionId === id);

    // @ts-ignore
    return action.isOptional;
  }

  render() {
    return (
      <div>
        {this.renderActionInputs()}
        {this.renderConfirmActions()}
      </div>
    );
  }

  renderActionInputs() {
    const actions = this.props.step.actions;

    return (
      <div className={workflowStepActionsInputsCN}>
        {actions
          .filter((action: WorkflowAction) => !isConfirmAction(action.type))
          .map((action: WorkflowAction) => {
            // action with new value
            const updatedAction = {
              ...action,
              value: this.props.collapsed
                ? action.value
                : this.state.actionValues[action.id].value || action.value,
            };
            return (
              <WorkflowActionContainer
                key={action.id}
                action={updatedAction}
                disabled={this.props.disabled}
                collapsed={this.props.collapsed}
                onInputSuccess={(value) =>
                  this.handleActionInput(action.id, action.type, value)
                }
                onInputFailure={(value) =>
                  this.handleActionInput(action.id, action.type, value)
                }
              />
            );
          })}
      </div>
    );
  }

  renderConfirmActions() {
    if (this.props.disabled) {
      return null;
    }

    if (stepShouldSwitchAutomatically(this.props.step)) {
      return null;
    }

    let confirmActions = this.props.step.actions.filter(({ type }) =>
      isConfirmAction(type),
    );

    if (!confirmActions.length) {
      confirmActions = [
        {
          label: "Next",
          id: "next",
          type: "CONFIRM",
          value: "",
          defaultValue: "",
        },
      ];
    }

    return (
      <div
        className={style({
          paddingTop: "24px",
          $nest: {
            "&>*": {
              marginTop: "8px",
              marginRight: "8px",
              display: "inline-block !important",
            },
            "&>*:last-child": {
              marginRight: "0",
            },
          },
        })}
      >
        {confirmActions.map(({ label, id, type }, i) => (
          <Button
            className={`workflow-step-confirm-button-${id}`}
            key={`confirmAction${i}`}
            label={label || "No Label"}
            onClick={() => this.handleStepSubmit(id)}
            disabled={type === "SKIP" ? false : !this.actionsAreValid()}
            type={i === 0 ? "primary" : "secondary"}
          />
        ))}
      </div>
    );
  }
}

function actionIsTrigger(actionType: string): boolean {
  return actionType === "SINGLE_FILE" || actionType === "UPLOAD_FILESET";
}

function isConfirmAction(actionType: string): boolean {
  return actionType === "CONFIRM" || actionType === "SKIP";
}
